import React, { useEffect, useState } from 'react';
import { getOrganizations, postInquire } from '../Services/ResellerInquiry.service';
import { object, string, boolean, ValidationError } from 'yup';
import { translate } from '../Services/translation';
import InputCheckbox from './InputCheckbox';
import InputRadio from './InputRadio';
import InputTextArea from './InputTextArea';
import { googleAnalytics } from '../Services/googleAnalytics';
import ConfiguratorPopUp from './Configurator/ConfiguratorPopUp';

const formValidationSchema = object().shape({
  selectedStore: string()
    .required(translate(`validation.required`))
    .notOneOf(['none'], translate(`validation.required`)),
  name: string().required(translate(`validation.required`)),
  email: string().required(translate(`validation.required`)).email(translate('validation.email')),
  phone: string().required(translate(`validation.required`)),
  isTosAccepted: boolean()
    .required(translate(`validation.checkrequired`))
    .oneOf([true], translate(`validation.checkrequired`))
});

const ResellerInquiryForm = ({
  items = [],
  channelId,
  pageId,
  information,
  tosText,
  accessGroups,
  totalPrice,
  analyticsEventName = 'sendWishlist',
  analyticsListName,
  onSuccessCallback,
  onResetConfigurator,
  isConfigurator = false,
}) => {
  const [organizations, setOrganizations] = useState([]);
  const [selectedCity, setSelectedCity] = useState('none');
  const [selectedStore, setSelectedStore] = useState('none');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [contactViaPhone, setContactViaPhone] = useState(false);
  const [contactViaEmail, setContactViaEmail] = useState(false);
  const [inquiryType, setInquiryType] = useState('price');
  const [message, setMessage] = useState('');
  const [isTosAccepted, setHasTosAccepted] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    let preffObject;
    const setup = async () => {
      if (items && items.length > 0) {
        setOrganizations(await getOrganizations(channelId));
        if (preffObject != null) {
          setSelectedCity(preffObject.city);
          setSelectedStore(preffObject.shopName + `[${preffObject.streetName}]`);
        }
      }
    };
    try {
      const preff = localStorage.getItem('preferedaddress');
      preffObject = JSON.parse(preff);
    } catch (error) {
      // Ignorera parse-fel
    } finally {
      setup();
    }
  }, [items.length, channelId]);

  const hasAccess = (arr, target) => target && target.every(v => arr && arr.includes(v));

  const onChosenCity = (ev) => {
    setSelectedCity(ev.target.value);
    setSelectedStore('none');
    setErrors((current) => ({ ...current, selectedStore: undefined }));
  };

  const clearError = (field) => setErrors((current) => ({ ...current, [field]: undefined }));

  const enableSubmit = () => selectedStore !== 'none';

  const onSubmit = async (ev) => {
    ev.preventDefault();
    try {
      let res = await formValidationSchema.validate({
        name,
        phone,
        email,
        contactViaPhone,
        contactViaEmail,
        inquiryType,
        message,
        isTosAccepted,
        selectedStore,
        channelId,
        pageId,
      }, { abortEarly: false });

      setErrors({});

      res = { ...res, rows: items };

      if (await postInquire(res, isConfigurator)) {
        setShowSuccessMessage(true);
        
        googleAnalytics({
          'event': analyticsEventName,
          'list_name': analyticsListName,
          'resellerCity': selectedCity,
          'reseller_id': organizations
            .find((x) => x.city === selectedCity)
            ?.organizations.find(x => x.id === selectedStore)?.dealerERPID,
          'reseller': organizations
            .find((x) => x.city === selectedCity)
            ?.organizations.find(x => x.id === selectedStore)?.shopName,
          'email_contact': contactViaEmail, 
          'telephone_contact': contactViaPhone,
          'offerRequestType': inquiryType, 
          'value': totalPrice
        });

        setSelectedCity('none');
        setSelectedStore('none');

        if (onSuccessCallback) {
          onSuccessCallback();
        }

        if (!isConfigurator) {
          setTimeout(() => {
            setShowSuccessMessage(false);
          }, 5000);
        }
      } else {
        setShowErrorMessage(true);
        setTimeout(() => {
          setShowErrorMessage(false);
        }, 5000);
      }
    } catch (e) {
      if (e instanceof ValidationError) {
        const nextErrors = {};
        e.inner.forEach((error) => {
          if (!nextErrors[error.path]) nextErrors[error.path] = error.message;
        });
        setErrors(nextErrors);
        return;
      }
      setShowErrorMessage(true);
      setTimeout(() => {
        setShowErrorMessage(false);
      }, 5000);
    }
  };

  const getColumnClass = (defaultClasses) => {
    return isConfigurator ? 'form-column' : defaultClasses;
  };

  return (
    <form onSubmit={onSubmit} noValidate>
      <div className={isConfigurator ? 'form-container' : 'row shoppinglist__addresses'}>
        {information && (
          <div
            className={getColumnClass('columns small-12 medium-6 large-4')}
            dangerouslySetInnerHTML={{ __html: information }}
          ></div>
        )}
        <div className={isConfigurator ? 'form-column grid' : 'columns small-12 medium-6 large-4'}>
          <div className="form__inputGroup">
            {isConfigurator && (
              <label htmlFor="reseller-city-select" className="form__label">
                {translate('brenderup.configurator.inquiry.selectcity')}
              </label>
            )}
            <select
              id="reseller-city-select"
              className="form__input"
              onChange={onChosenCity}
              value={selectedCity}
              aria-label={translate('shoppinglist.selectcity')}
            >
              <option disabled value="none">
                {translate('shoppinglist.selectcity')}
              </option>
              {organizations
                .filter(o => o.organizations && o.organizations.some(oo => hasAccess(oo.accessGroups, accessGroups)))
                .map((x) => (
                  <option key={x.city} value={x.city}>
                    {x.city}
                  </option>
                ))}
            </select>
          </div>
          <div className="form__inputGroup">
            {isConfigurator && (
              <label htmlFor="reseller-store-select" className="form__label">
                {translate('brenderup.configurator.inquiry.selectstore')}
              </label>
            )}
            <select
              id="reseller-store-select"
              className="form__input"
              onChange={(ev) => { setSelectedStore(ev.target.value); clearError('selectedStore'); }}
              value={selectedStore}
              aria-label={translate('shoppinglist.selectstore')}
              aria-invalid={!!errors.selectedStore}
            >
              <option disabled value="none">
                {translate('shoppinglist.selectstore')}
              </option>
              {organizations
                .find((x) => x.city === selectedCity)
                ?.organizations.filter(o => hasAccess(o.accessGroups, accessGroups))
                .map((x) => (
                  <option key={x.id} value={x.id}>
                    {x.shopName}
                  </option>
                ))}
            </select>
            {errors.selectedStore && <span className="form__validator--error">{errors.selectedStore}</span>}
          </div>
          <div className="form__inputGroup">
            {isConfigurator && (
              <label htmlFor="reseller-name-input" className="form__label">
                {translate('brenderup.configurator.inquiry.fullname')}
              </label>
            )}
            <input
              id="reseller-name-input"
              className="form__input"
              placeholder={translate('shoppinglist.yourname')}
              aria-label={translate('shoppinglist.yourname')}
              aria-invalid={!!errors.name}
              onChange={(ev) => { setName(ev.target.value); clearError('name'); }}
            />
            {errors.name && <span className="form__validator--error">{errors.name}</span>}
          </div>
          <div className="form__inputGroup">
            {isConfigurator && (
              <label htmlFor="reseller-phone-input" className="form__label">
                {translate('brenderup.configurator.inquiry.phone')}
              </label>
            )}
            <input
              id="reseller-phone-input"
              className="form__input"
              placeholder={translate('shoppinglist.yourphone')}
              aria-label={translate('shoppinglist.yourphone')}
              type="tel"
              aria-invalid={!!errors.phone}
              onChange={(ev) => { setPhone(ev.target.value); clearError('phone'); }}
            />
            {errors.phone && <span className="form__validator--error">{errors.phone}</span>}
          </div>
          <div className="form__inputGroup">
            {isConfigurator && (
              <label htmlFor="reseller-email-input" className="form__label">
                {translate('brenderup.configurator.inquiry.email')}
              </label>
            )}
            <input
              id="reseller-email-input"
              className="form__input"
              placeholder={translate('shoppinglist.youremail')}
              aria-label={translate('shoppinglist.youremail')}
              type="email"
              aria-invalid={!!errors.email}
              onChange={(ev) => { setEmail(ev.target.value); clearError('email'); }}
            />
            {errors.email && <span className="form__validator--error">{errors.email}</span>}
          </div>
        </div>
        <div className={isConfigurator ? 'form-column grid-2' : 'columns small-12 medium-6 large-4'}>
          <div className="form__inputGroup">
            <p>{translate('shoppinglist.wanttobecontactedvia')}</p>
            <div className="shoppinglist__addresses--contact">
              <InputCheckbox
                name="contactViaEmail"
                title={translate('shoppinglist.email')}
                className="contact-check"
                onChange={(ev) => setContactViaEmail(ev.target.value === 'on')}
              />
              <InputCheckbox
                name="contactViaPhone"
                title={translate('shoppinglist.phone')}
                className="contact-check"
                onChange={(ev) => setContactViaPhone(ev.target.value === 'on')}
              />
            </div>
          </div>
          <fieldset className="form__inputGroup">
            <legend>{translate('shoppinglist.whattypeofinquery')}</legend>
            <div className="shoppinglist__addresses--type">
              <div className="lists">
                <InputRadio
                  name="inquiryType"
                  title={translate('shoppinglist.price')}
                  value="price"
                  onChange={(ev) => setInquiryType(ev.target.value)}
                  checked={inquiryType === 'price'}
                />
              </div>
              <div className="lists">
                <InputRadio
                  name="inquiryType"
                  title={translate('shoppinglist.service')}
                  value="service"
                  onChange={(ev) => setInquiryType(ev.target.value)}
                  checked={inquiryType === 'service'}
                />
              </div>
              <div className="lists">
                <InputRadio
                  name="inquiryType"
                  title={translate('shoppinglist.inventory')}
                  value="inventory"
                  onChange={(ev) => setInquiryType(ev.target.value)}
                  checked={inquiryType === 'inventory'}
                />
              </div>
              <div className="lists">
                <InputRadio
                  name="inquiryType"
                  title={translate('shoppinglist.other')}
                  value="other"
                  onChange={(ev) => setInquiryType(ev.target.value)}
                  checked={inquiryType === 'other'}
                />
              </div>
            </div>
          </fieldset>
          <div className="form__inputGroup">
            {isConfigurator && (
              <label htmlFor="reseller-message-input" className="form__label">
                {translate('brenderup.configurator.inquiry.messagetostore')}
              </label>
            )}
            <InputTextArea
              id="reseller-message-input"
              placeholder={translate('shoppinglist.messagetocustomerservice')}
              aria-label={translate('shoppinglist.messagetocustomerservice')}
              onChange={(ev) => setMessage(ev.target.value)}
            />
          </div>
          <div className="form__inputGroup">
            <div className="input-wrapper">
              <InputCheckbox
                name="approveIntegrityPolicy"
                htmlTitle={tosText}
                onChange={() => { setHasTosAccepted(!isTosAccepted); clearError('isTosAccepted'); }}
              />
            </div>
            {errors.isTosAccepted && <span className="form__validator--error">{errors.isTosAccepted}</span>}
          </div>
          <button className={`shoppinglist__submit ${!enableSubmit() ? 'disabled' : ''}`} type="submit">
            {translate('shoppinglist.send')}
          </button>
          {showSuccessMessage && isConfigurator ? (
            <ConfiguratorPopUp
              isOpen={true}
              title={translate('brenderup.configurator.inquiry.sent.title')}
              text={translate('brenderup.configurator.inquiry.sent.text')}
              confirmText={translate('brenderup.configurator.button.newinquiry')}
              resetText={translate('brenderup.configurator.button.clearconfiguration')}
              onConfirm={() => setShowSuccessMessage(false)}
              onCancel={() => {
                setShowSuccessMessage(false);
                if (onResetConfigurator) onResetConfigurator();
              }}
              onClose={() => setShowSuccessMessage(false)}
            />
          ) : showSuccessMessage ? (
            <p>{translate('shoppinglist.submitsuccess')}</p>
          ) : null}
          {showErrorMessage && 
            <p>{translate('shoppinglist.submiterror')}</p>
          }
          {isConfigurator && (
            <p className="configurator__summary-submit-info">
              {translate('brenderup.configurator.inquiry.submit.info')}
            </p>
          )}
        </div>
      </div>
    </form>
  );
};

export default ResellerInquiryForm;